import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { EmptyState } from "../components/EmptyState";
import {
  listPartnerApplications,
  type PartnerApplicationRow,
  type PartnerApplicationStatusFilter,
} from "../api/partnerApplications";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";

// Partner-application queue.
//
// Bound to GET /v2/admin/bff/affiliate/partners/applications. Browser
// carries the bearer session only — no HMAC, no service secret.
//
// Approve/reject/suspend (on the detail page) are `low_config` on the BE
// and REQUIRE an approved multisign proposalId. This surface does NOT
// create proposals — the operator creates + approves a low_config
// proposal out of band, then pastes the approved proposalId on the
// detail page.

function shorten(v: string | null): string {
  if (!v) return "—";
  if (v.startsWith("0x") && v.length === 42) return `${v.slice(0, 6)}…${v.slice(-4)}`;
  if (v.length <= 18) return v;
  return `${v.slice(0, 10)}…${v.slice(-4)}`;
}

function statusBadge(s: PartnerApplicationRow["status"]) {
  switch (s) {
    case "pending":
      return { label: "Pending", color: "var(--accent, #2c7be5)" };
    case "approved":
      return { label: "Approved", color: "#0a9444" };
    case "rejected":
      return { label: "Rejected", color: "#d23f3f" };
    case "suspended":
      return { label: "Suspended", color: "#b58900" };
  }
}

function PartnerApplicationsInner() {
  const [rows, setRows] = useState<PartnerApplicationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] =
    useState<PartnerApplicationStatusFilter>("pending");

  const load = useCallback(async (filter: PartnerApplicationStatusFilter) => {
    setError(null);
    setLoading(true);
    try {
      const r = await listPartnerApplications({ status: filter });
      setRows(r.items);
    } catch (e) {
      setError(mapAffiliateError(e).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(statusFilter);
  }, [load, statusFilter]);

  return (
    <div>
      <details style={{ margin: "8px 0" }}>
        <summary style={{ fontSize: 12, color: "var(--fg-dim)", cursor: "pointer" }}>
          Advanced — endpoint &amp; notes
        </summary>
        <p style={{ margin: "6px 0", fontSize: 12, color: "var(--fg-dim)" }}>
          Bound to <code>GET /v2/admin/bff/affiliate/partners/applications</code> —
          bearer session + trusted device. Users submit applications from the
          affiliate dashboard; operators review them here. Approve / reject /
          suspend require an approved <code>low_config</code> proposalId.
        </p>
      </details>

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
        <label htmlFor="pa-status" style={{ fontSize: 12, color: "var(--fg-dim)" }}>
          Status:
        </label>
        <select
          id="pa-status"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as PartnerApplicationStatusFilter)
          }
          style={{ padding: "4px 8px", fontSize: 13 }}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
          <option value="all">All</option>
        </select>
        <button
          type="button"
          onClick={() => void load(statusFilter)}
          disabled={loading}
          style={{ padding: "4px 12px", fontSize: 13 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            border: "1px solid #d23f3f",
            background: "rgba(210, 63, 63, 0.06)",
            color: "#d23f3f",
            margin: "12px 0",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {rows && rows.length === 0 && (
        <EmptyState
          title={
            statusFilter === "pending"
              ? "No pending partner applications."
              : statusFilter === "all"
                ? "No partner applications yet."
                : `No ${statusFilter} partner applications.`
          }
          hint="Applications submitted from the affiliate dashboard appear here for review."
        />
      )}

      {rows && rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              minWidth: 900,
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border, #ccc)", textAlign: "left" }}>
                <th style={{ padding: "8px 6px" }}>id</th>
                <th style={{ padding: "8px 6px" }}>identityId</th>
                <th style={{ padding: "8px 6px" }}>Applicant wallet</th>
                <th style={{ padding: "8px 6px" }}>Status</th>
                <th style={{ padding: "8px 6px" }}>Created</th>
                <th style={{ padding: "8px 6px" }}>Reviewed</th>
                <th style={{ padding: "8px 6px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = statusBadge(r.status);
                const href = `/admin/affiliate/partners/${encodeURIComponent(r.id)}`;
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--border-faint, #eee)" }}>
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 12 }}>
                      <Link to={href} style={{ color: "var(--accent, #2c7be5)" }}>
                        {r.id}
                      </Link>
                    </td>
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}>
                      {r.identityId ?? "—"}
                    </td>
                    <td
                      style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}
                      title={r.applicantWalletAddress ?? ""}
                    >
                      {shorten(r.applicantWalletAddress)}
                    </td>
                    <td style={{ padding: "8px 6px", color: badge.color, fontWeight: 600 }}>
                      {badge.label}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11 }}>
                      {r.createdAt ? <code>{r.createdAt}</code> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11 }}>
                      {r.reviewedAt ? <code>{r.reviewedAt}</code> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px" }}>
                      <Link
                        to={href}
                        style={{
                          fontSize: 11,
                          padding: "2px 6px",
                          border: "1px solid var(--border, #ccc)",
                          borderRadius: 3,
                          textDecoration: "none",
                          color: "var(--fg, inherit)",
                        }}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PartnerApplications() {
  return (
    <div>
      <PageHeader
        title="Partner applications"
        subtitle="Operator queue for partner-application review. Approve / reject / suspend are low_config — they require an approved multisign proposalId (created + approved out of band, pasted on the detail page)."
        method="GET"
        path="/v2/admin/bff/affiliate/partners/applications"
      />
      <AuthRequired>
        <PartnerApplicationsInner />
      </AuthRequired>
    </div>
  );
}
